/**
 * "Applies to" scope for documentation pages.
 *
 * Every docs page says whether it documents the gateway runtime (API, AI, and
 * MCP gateways) or the Developer Portal, and which hosting options it holds
 * for. Pages set both in their own `applies_to` frontmatter, so the scope moves
 * with the file:
 *
 * ```yaml
 * applies_to:
 *   product: gateway # developer-portal, or a list of both
 *   hosting: all # or a list of hosting ids
 *   differs:
 *     managed-dedicated: Markdown describing what changes on this option.
 *   plan: enterprise # optional
 * ```
 *
 * Only the pages matched by `EXEMPT_PATHS` (home, tutorials, and pages that
 * aren't product docs) go without one.
 *
 * The same resolved scope drives the rendered `<AppliesTo>` banner, the page
 * metadata (meta tags and JSON-LD), and the preamble written into the
 * Markdown export and llms.txt files that agents read.
 */

export const PRODUCTS = {
  gateway: {
    label: "Gateway",
    summary: "runtime for API, AI, and MCP gateways",
  },
  "developer-portal": {
    label: "Developer Portal",
    summary: "the documentation site for your API consumers",
  },
} as const;

export const HOSTING = {
  "managed-edge": {
    label: "Managed Edge",
    href: "/managed-edge/overview",
  },
  "managed-dedicated": {
    label: "Managed Dedicated",
    href: "/dedicated/overview",
  },
  "self-hosted": {
    label: "Self-Hosted",
    href: "/self-hosted/overview",
  },
} as const;

export const PLANS = {
  enterprise: { label: "Enterprise add-on" },
} as const;

export type ProductId = keyof typeof PRODUCTS;
export type HostingId = keyof typeof HOSTING;
export type PlanId = keyof typeof PLANS;

export const PRODUCT_IDS = Object.keys(PRODUCTS) as ProductId[];
export const HOSTING_IDS = Object.keys(HOSTING) as HostingId[];

export interface HostingNote {
  hosting: HostingId;
  /** Markdown */
  note: string;
}

export interface Scope {
  products: ProductId[];
  hosting: HostingId[];
  differs: HostingNote[];
  plan?: PlanId;
}

interface ScopeInput {
  product?: ProductId | ProductId[];
  hosting?: "all" | HostingId[];
  differs?: Partial<Record<HostingId, string>>;
  plan?: PlanId;
}

/**
 * Doc paths (relative to `docs/`, no extension) that don't need `applies_to`:
 * the home page, the getting-started tutorials, and pages that aren't product
 * docs.
 */
export const EXEMPT_PATHS: RegExp[] = [
  /^home$/,
  /^getting-started\//,
  /^ask$/,
  /^conferences\//,
];

export const isExempt = (docPath: string) =>
  EXEMPT_PATHS.some((pattern) => pattern.test(docPath));

export class AppliesToError extends Error {}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toList = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [value];

const parseInput = (raw: unknown, where: string): ScopeInput => {
  if (!isRecord(raw)) {
    throw new AppliesToError(
      `${where}: applies_to must be an object with product and hosting.`,
    );
  }

  const known = new Set(["product", "hosting", "differs", "plan"]);
  for (const key of Object.keys(raw)) {
    if (!known.has(key)) {
      throw new AppliesToError(
        `${where}: unknown applies_to key "${key}". Use product, hosting, differs, or plan.`,
      );
    }
  }

  const input: ScopeInput = {};

  if (raw.product !== undefined) {
    const products = toList(raw.product);
    for (const product of products) {
      if (!PRODUCT_IDS.includes(product as ProductId)) {
        throw new AppliesToError(
          `${where}: unknown product "${String(product)}". Use one of ${PRODUCT_IDS.join(", ")}.`,
        );
      }
    }
    input.product = products as ProductId[];
  }

  if (raw.hosting !== undefined) {
    if (raw.hosting === "all") {
      input.hosting = "all";
    } else {
      const hosting = toList(raw.hosting);
      if (hosting.length === 0) {
        throw new AppliesToError(`${where}: hosting can't be empty.`);
      }
      for (const option of hosting) {
        if (!HOSTING_IDS.includes(option as HostingId)) {
          throw new AppliesToError(
            `${where}: unknown hosting option "${String(option)}". Use "all" or a list of ${HOSTING_IDS.join(", ")}.`,
          );
        }
      }
      input.hosting = hosting as HostingId[];
    }
  }

  if (raw.differs !== undefined) {
    if (!isRecord(raw.differs)) {
      throw new AppliesToError(
        `${where}: differs must map hosting options to a note.`,
      );
    }
    for (const [option, note] of Object.entries(raw.differs)) {
      if (!HOSTING_IDS.includes(option as HostingId)) {
        throw new AppliesToError(
          `${where}: unknown hosting option "${option}" in differs.`,
        );
      }
      if (typeof note !== "string" || note.trim() === "") {
        throw new AppliesToError(
          `${where}: differs.${option} must be a non-empty string.`,
        );
      }
    }
    input.differs = raw.differs as Partial<Record<HostingId, string>>;
  }

  if (raw.plan !== undefined) {
    if (!(String(raw.plan) in PLANS)) {
      throw new AppliesToError(
        `${where}: unknown plan "${String(raw.plan)}". Use one of ${Object.keys(PLANS).join(", ")}.`,
      );
    }
    input.plan = raw.plan as PlanId;
  }

  return input;
};

/**
 * Resolves the scope of a page from its `applies_to` frontmatter. Returns
 * `undefined` for an exempt page without one. Throws `AppliesToError` when the
 * frontmatter is missing or invalid.
 *
 * @param docPath Path relative to `docs/`, without extension, for example
 * `guides/proxying-between-zuplo-gateways`.
 */
export const resolveScope = (
  docPath: string,
  frontmatter: unknown,
): Scope | undefined => {
  if (frontmatter === undefined || frontmatter === null) {
    if (isExempt(docPath)) return undefined;
    throw new AppliesToError(
      `${docPath}: missing applies_to frontmatter. Add product and hosting, for example:\n` +
        "applies_to:\n  product: gateway\n  hosting: all",
    );
  }

  const merged = parseInput(frontmatter, docPath);
  if (!merged.product || !merged.hosting) {
    throw new AppliesToError(
      `${docPath}: applies_to needs both product and hosting.`,
    );
  }

  const hosting =
    merged.hosting === "all"
      ? [...HOSTING_IDS]
      : HOSTING_IDS.filter((id) =>
          (merged.hosting as HostingId[]).includes(id),
        );

  const differs: HostingNote[] = [];
  for (const id of HOSTING_IDS) {
    const note = merged.differs?.[id];
    if (note === undefined) continue;
    if (!hosting.includes(id)) {
      throw new AppliesToError(
        `${docPath}: differs.${id} is set, but the page doesn't apply to ${HOSTING[id].label}.`,
      );
    }
    differs.push({ hosting: id, note: note.trim() });
  }

  return {
    products: merged.product
      ? PRODUCT_IDS.filter((id) => toList(merged.product).includes(id))
      : [],
    hosting,
    differs,
    ...(merged.plan ? { plan: merged.plan } : {}),
  };
};

const joinLabels = (labels: string[]): string =>
  labels.length <= 2
    ? labels.join(" and ")
    : `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;

export const productLabels = (scope: Scope): string =>
  joinLabels(scope.products.map((id) => PRODUCTS[id].label));

/** The hosting options a page doesn't apply to. */
export const unavailableHosting = (hosting: HostingId[] | null): HostingId[] =>
  hosting ? HOSTING_IDS.filter((id) => !hosting.includes(id)) : [];

/** "Not available on Managed Edge or Self-Hosted", or `undefined`. */
export const unavailableSummary = (
  hosting: HostingId[] | null,
): string | undefined => {
  const labels = unavailableHosting(hosting).map((id) => HOSTING[id].label);
  if (labels.length === 0) return undefined;
  return `Not available on ${
    labels.length <= 2
      ? labels.join(" or ")
      : `${labels.slice(0, -1).join(", ")}, or ${labels.at(-1)}`
  }`;
};

const PRODUCT_SENTENCE: Record<string, string> = {
  gateway:
    "This page documents the Zuplo **gateway** runtime (API, AI, and MCP gateways), not the Developer Portal.",
  "developer-portal":
    "This page documents the Zuplo **Developer Portal** (the documentation site for your API consumers), not the gateway runtime.",
  both: "This page documents both the Zuplo **gateway** runtime (API, AI, and MCP gateways) and the **Developer Portal** (the documentation site for your API consumers).",
};

const HOSTING_OPTIONS_URL = "https://zuplo.com/docs/articles/hosting-options";

/** Human-readable hosting summary, or `undefined` when unstated. */
export const hostingSummary = (
  hosting: HostingId[] | null,
): string | undefined => {
  if (!hosting) return undefined;
  const labels = joinLabels(hosting.map((id) => HOSTING[id].label));
  return hosting.length === HOSTING_IDS.length ? labels : `${labels} only`;
};

/**
 * The block quote written under the H1 of each page's Markdown export and in
 * llms-full.txt. It's written to make sense to an agent that lands on the page
 * with no other context. Notes keep their Markdown so links stay usable.
 */
export const formatScopePreamble = (scope: Scope): string => {
  const lines: string[] = ["**Scope of this page**"];
  if (scope.products.length > 0) {
    lines.push(
      PRODUCT_SENTENCE[
        scope.products.length === 1 ? scope.products[0]! : "both"
      ]!,
    );
  }
  const labels = joinLabels(scope.hosting.map((id) => HOSTING[id].label));
  const unavailable = unavailableHosting(scope.hosting).map(
    (id) => HOSTING[id].label,
  );
  lines.push(
    unavailable.length === 0
      ? `It applies to every Zuplo hosting option: ${labels}. See [Hosting options](${HOSTING_OPTIONS_URL}).`
      : `It applies only to ${labels} hosting. It doesn't apply to ${
          unavailable.length <= 2
            ? unavailable.join(" or ")
            : `${unavailable.slice(0, -1).join(", ")}, or ${unavailable.at(-1)}`
        }, so don't use it for those deployments. See [Hosting options](${HOSTING_OPTIONS_URL}).`,
  );
  for (const { hosting: id, note } of scope.differs) {
    lines.push(
      `On ${HOSTING[id].label}, this works differently: ${note.replaceAll(/\s+/g, " ")}`,
    );
  }
  if (scope.plan) {
    lines.push(`It requires the ${PLANS[scope.plan].label}.`);
  }
  return lines.map((line) => `> ${line}`).join("\n>\n");
};

/** The short tag appended to a page's entry in llms.txt. */
export const formatScopeTag = (scope: Scope): string => {
  const parts: string[] = [];
  if (scope.products.length > 0) {
    parts.push(`applies to ${productLabels(scope)}`);
  }
  if (scope.hosting) {
    parts.push(
      scope.hosting.length === HOSTING_IDS.length
        ? "all hosting"
        : `${joinLabels(scope.hosting.map((id) => HOSTING[id].label))} only`,
    );
  }
  if (scope.differs.length > 0) {
    parts.push(
      `differs on ${joinLabels(scope.differs.map(({ hosting }) => HOSTING[hosting].label))}`,
    );
  }
  if (scope.plan) parts.push(PLANS[scope.plan].label);
  return `[${parts.join("; ")}]`;
};

/** A section marker (`<AppliesTo hosting="..." />`) as Markdown for agents. */
export const formatSectionPreamble = (hosting: HostingId[]): string =>
  `> This section applies only to ${joinLabels(hosting.map((id) => HOSTING[id].label))} hosting.`;
